import { VerificationModal } from "@/components/auth/verification-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { RoleBadge } from "@/components/users/role-badge";
import {
  DemoAccount,
  useDemoAccounts,
  useStartDemoLogin,
} from "@/lib/api/demo-accounts";
import { useLoginAuthStore } from "@/lib/hooks/use-login-auth-store";
import { touchLastActivity } from "@/lib/inactivity";
import { OTPsSendResponse } from "@stytch/vanilla-js";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";

/** Surfaces the API's own message (409 not provisioned, 429 throttled, 502…). */
const resolveStartLoginError = (error: unknown): string => {
  const apiMessage = (
    error as { response?: { data?: { error?: string } } } | undefined
  )?.response?.data?.error;

  if (apiMessage) return apiMessage;

  return error instanceof Error
    ? error.message
    : "Something went wrong starting the login";
};

const errorToast = (title: string, description: string) => ({
  title,
  description,
  className:
    "bottom-0 left-0 flex fixed md:max-w-[420px] md:bottom-4 md:left-4 border-2 border-red-500 bg-red-50 text-red-900",
});

function AccountRow({
  account,
  onLogin,
  isLoggingIn,
  loginBusy,
}: {
  account: DemoAccount;
  onLogin: (account: DemoAccount) => void;
  isLoggingIn: boolean;
  loginBusy: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-[12px] border border-[#e3dbcf] bg-white/80 p-4 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-black">
            {account.displayName}
          </span>
          {account.systemRoles.map((role) => (
            <RoleBadge key={role} role={role} size="sm" />
          ))}
          {!account.seeded && (
            <Badge className="border border-[#e3dbcf] bg-[#fbf4ea] text-xs text-[#9a8f80]">
              not seeded
            </Badge>
          )}
        </div>

        <p className="mt-1 text-sm text-[#9a8f80]">{account.summary}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {account.canUseDashboard ? (
          <Button
            size="sm"
            className="rounded-[12px] bg-black px-4 font-semibold text-white hover:bg-black/90"
            onClick={() => onLogin(account)}
            disabled={loginBusy || !account.seeded}
          >
            {isLoggingIn && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Log in
          </Button>
        ) : (
          <span className="max-w-[13rem] text-right text-xs text-[#9a8f80]">
            Login through the app
          </span>
        )}
      </div>
    </div>
  );
}

export function DemoAccountsPicker() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { toast } = useToast();
  const accountsQuery = useDemoAccounts();
  const startLogin = useStartDemoLogin();

  const {
    otpResponse,
    phoneNumber,
    verificationModalOpen,
    setOtpData,
    setOtpResponse,
    setPhoneNumber,
    setVerificationModalOpen,
    resetLoginState,
  } = useLoginAuthStore();

  const [pendingLoginKey, setPendingLoginKey] = useState<string | null>(null);
  // Which account the open code modal belongs to, so resend targets it.
  const [activeLoginKey, setActiveLoginKey] = useState<string | null>(null);

  // Ignore completions from a start-login that was superseded or cancelled.
  const loginRequestIdRef = useRef(0);
  const loginInFlightRef = useRef(false);

  const requireMfa = import.meta.env.VITE_REQUIRE_MFA === "true";

  const loginBusy =
    pendingLoginKey !== null || startLogin.isPending || verificationModalOpen;

  const invalidateLoginRequest = () => {
    loginRequestIdRef.current += 1;
    loginInFlightRef.current = false;
    setPendingLoginKey(null);
  };

  const onLogin = useCallback(
    async (account: DemoAccount) => {
      if (loginInFlightRef.current) return;

      loginInFlightRef.current = true;
      const requestId = ++loginRequestIdRef.current;
      setPendingLoginKey(account.key);

      try {
        const { method_id } = await startLogin.mutateAsync(account.key);

        if (requestId !== loginRequestIdRef.current) return;

        // VerificationModal only needs method_id off this object
        setActiveLoginKey(account.key);
        setOtpData({ method_id } as OTPsSendResponse, account.phone);
      } catch (error) {
        if (requestId !== loginRequestIdRef.current) return;

        toast(
          errorToast(
            `Could not start login for ${account.displayName}`,
            resolveStartLoginError(error),
          ),
        );
      } finally {
        if (requestId === loginRequestIdRef.current) {
          loginInFlightRef.current = false;
          setPendingLoginKey(null);
        }
      }
    },
    [setOtpData, startLogin, toast],
  );

  const onResend = useCallback(async () => {
    if (!activeLoginKey) throw new Error("No demo login in progress");

    const requestId = loginRequestIdRef.current;
    const key = activeLoginKey;
    const { method_id } = await startLogin.mutateAsync(key);

    if (requestId !== loginRequestIdRef.current) {
      throw new Error("Demo login was cancelled");
    }

    return { method_id } as OTPsSendResponse;
  }, [activeLoginKey, startLogin]);

  const onLoginSuccess = useCallback(() => {
    resetLoginState();
    touchLastActivity();
    queryClient.invalidateQueries({ queryKey: ["current-user"] });
    // Same as password login: the onboarding gate reads this user-scoped cache.
    queryClient.removeQueries({
      queryKey: ["mentor-profile"],
    });

    setTimeout(() => {
      router.navigate({ to: "/" });
    }, 1000);
  }, [queryClient, resetLoginState, router]);

  const hasUnseeded = accountsQuery.data?.some((account) => !account.seeded);

  return (
    <div className="flex min-h-screen w-full flex-row bg-[#fbf4ea]">
      <div className="flex w-full flex-col items-center px-6 py-12 lg:w-1/2">
        <div className="my-auto w-full max-w-[520px]">
          <Link
            to="/login"
            className="text-sm font-medium text-[#9a8f80] hover:text-black hover:underline"
          >
            Back to login
          </Link>

          <h1 className="mt-6 text-4xl font-semibold leading-tight text-black">
            Demo accounts
          </h1>
          <p className="mt-1 text-2xl font-semibold text-[#9a8f80]">
            Pick a role to log in
          </p>
          <p className="mt-3 text-sm text-[#9a8f80]">
            Logging in sends an OTP to the account&apos;s phone number, which is
            relayed to the <code>#dev-login-otp</code> Slack channel.
          </p>

          {requireMfa && (
            <div className="mt-4 rounded-[12px] border-2 border-red-500 bg-red-50 p-3 text-sm text-red-900">
              <strong>VITE_REQUIRE_MFA is enabled.</strong> Phone-OTP alone
              produces a single authentication factor, which the authenticated
              layout rejects, so these logins will not hold. Disable MFA for
              this environment.
            </div>
          )}

          {accountsQuery.isError && (
            <div className="mt-4 rounded-[12px] border-2 border-red-500 bg-red-50 p-3 text-sm text-red-900">
              Could not load demo accounts. This environment may have them
              disabled.
            </div>
          )}

          {accountsQuery.isLoading && (
            <div className="mt-8 flex items-center gap-2 text-sm text-[#9a8f80]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading accounts…
            </div>
          )}

          {accountsQuery.data && (
            <>
              {hasUnseeded && (
                <div className="mt-4 rounded-[12px] border border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
                  Some accounts are not seeded in this environment yet. A super
                  admin can provision them from <strong>Settings → Dev</strong>.
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3">
                {accountsQuery.data.map((account) => (
                  <AccountRow
                    key={account.key}
                    account={account}
                    onLogin={onLogin}
                    loginBusy={loginBusy}
                    isLoggingIn={
                      pendingLoginKey === account.key ||
                      (startLogin.isPending && activeLoginKey === account.key)
                    }
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="hidden items-center justify-center bg-[#fbf4ea] lg:flex lg:w-1/2">
        <img
          src="assets/Illustrations-6.png"
          alt="Somethings illustration"
          className="h-auto w-full max-w-[520px]"
        />
      </div>

      <VerificationModal
        open={verificationModalOpen}
        onClose={() => {
          invalidateLoginRequest();
          setVerificationModalOpen(false);
          setOtpResponse(null);
          setPhoneNumber(null);
          setActiveLoginKey(null);
        }}
        otpResponse={otpResponse as OTPsSendResponse | null}
        setOtpResponse={setOtpResponse}
        phoneNumber={phoneNumber ?? ""}
        handleSuccess={onLoginSuccess}
        onResend={onResend}
      />
    </div>
  );
}
